import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Minus, Plus, Trash2, AlertTriangle } from "lucide-react";
import { po, money } from "./api";
import { MenuSearch } from "./MenuSearch";

const Step = ({ n, title, children }) => (
  <section className="space-y-3 rounded-2xl border-2 border-[#f1dca6] bg-[#fffbf2] p-4">
    <h3 className="flex items-center gap-2 text-lg font-semibold text-[#7a0f1d]"><span className="grid h-7 w-7 place-items-center rounded-full bg-[#7a0f1d] text-sm text-[#fff3c4]">{n}</span>{title}</h3>
    {children}
  </section>
);

function Totals({ lines, menu }) {
  const sym = menu.currency_symbol;
  const food = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const pack = lines.reduce((s, l) => s + l.packaging * l.qty, 0);
  const gst = Math.round(food * menu.gst_percent) / 100;
  const row = (l, v, tid, big) => <div className={`flex justify-between ${big ? "border-t-2 border-[#e2b85a] pt-2 text-xl font-bold text-[#7a0f1d]" : "text-base"}`} data-testid={tid}><span>{l}</span><span>{money(sym, v)}</span></div>;
  return (
    <div className="space-y-1">
      {row("Food Subtotal", food, "po-calc-food")}
      {row("Packaging", pack, "po-calc-packaging")}
      {row(`${menu.gst_label} (${menu.gst_percent}% on food)`, gst, "po-calc-gst")}
      {row("FINAL AMOUNT PAYABLE", food + pack + gst, "po-calc-total", true)}
    </div>
  );
}

export function PickupOrderForm({ token, center, order, onClose, onSaved, inline }) {
  const [menu, setMenu] = useState(null);
  const [f, setF] = useState({ customer_name: order?.customer_name || "", phone: order?.phone || "", pickup_date: order?.pickup_date || "", pickup_time: order?.pickup_time || "", notes: order?.notes || "" });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    po("menu", token, { center }).then((m) => {
      setMenu(m);
      if (order) setLines(order.items.map((i) => { const mm = m.items.find((x) => x.key === i.key); return mm ? { ...mm, qty: i.qty } : null; }).filter(Boolean));
    }).catch((e) => toast.error(e.message));
  }, [token, center, order]);
  const add = (m) => setLines((ls) => (ls.some((l) => l.key === m.key) ? ls.map((l) => (l.key === m.key ? { ...l, qty: l.qty + 1 } : l)) : [...ls, { ...m, qty: 1 }]));
  const setQty = (key, q) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, qty: Math.max(1, q) } : l)));
  const save = async () => {
    if (!f.customer_name.trim() || !f.phone.trim()) return toast.error("Enter customer name and mobile number");
    if (!lines.length) return toast.error("Add at least one dish");
    setSaving(true);
    try {
      const body = { center, ...f, items: lines.map((l) => ({ key: l.key, name: l.name, qty: l.qty })) };
      const r = await po(order ? "update" : "create", token, order ? { ...body, id: order.id } : body);
      toast.success(order ? "Order updated" : `Order ${r.order.ref} created`);
      onSaved(r.order);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const fld = (k, label, props = {}) => <div className="space-y-1"><Label className="text-base">{label}</Label><Input className="h-12 text-base" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`po-form-${k}`} {...props} /></div>;
  const body = (
    <>
        {!menu ? <Loader2 className="mx-auto h-8 w-8 animate-spin" /> : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Centre: <b>{menu.centre.name}</b> ({center})</p>
            {menu.payment_warning && <p className="flex gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="po-payment-warning"><AlertTriangle className="h-5 w-5 shrink-0" />{menu.payment_warning}</p>}
            <Step n="1" title="Customer">
              <div className="grid gap-3 sm:grid-cols-2">
                {fld("customer_name", "Customer name *")}
                {fld("phone", "Mobile / WhatsApp *", { inputMode: "tel" })}
                {fld("pickup_date", "Pickup date (if known)", { type: "date" })}
                {fld("pickup_time", "Pickup time (if known)", { type: "time" })}
              </div>
            </Step>
            <Step n="2" title="Select food">
              <MenuSearch items={menu.items} sym={menu.currency_symbol} onAdd={add} />
              {lines.map((l) => (
                <div key={l.key} className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-3 shadow-sm" data-testid={`po-line-${l.key}`}>
                  <div className="min-w-[160px] flex-1"><p className="font-semibold">{l.name}</p><p className="text-sm text-muted-foreground">{money(menu.currency_symbol, l.price)} each{l.serves ? ` · ${l.serves}` : ""}</p></div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-10 w-10" onClick={() => setQty(l.key, l.qty - 1)} data-testid={`po-line-minus-${l.key}`}><Minus className="h-4 w-4" /></Button>
                    <Input className="h-10 w-16 text-center text-base" value={l.qty} onChange={(e) => setQty(l.key, parseInt(e.target.value, 10) || 1)} data-testid={`po-line-qty-${l.key}`} />
                    <Button size="icon" variant="outline" className="h-10 w-10" onClick={() => setQty(l.key, l.qty + 1)} data-testid={`po-line-plus-${l.key}`}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <span className="w-28 text-right text-base font-semibold">{money(menu.currency_symbol, l.price * l.qty)}</span>
                  <Button size="icon" variant="ghost" onClick={() => setLines(lines.filter((x) => x.key !== l.key))} data-testid={`po-line-remove-${l.key}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>))}
              {!lines.length && <p className="text-sm text-muted-foreground">Search above and tap a dish to add it.</p>}
            </Step>
            <Step n="3" title="Total"><Totals lines={lines} menu={menu} /></Step>
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="h-12 px-6 text-base" onClick={onClose} data-testid="po-form-cancel">Cancel</Button>
              <Button className="h-12 bg-[#0b5a2e] px-8 text-base hover:bg-[#084524]" onClick={save} disabled={saving} data-testid="po-form-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Enquiry</Button>
            </div>
          </div>)}
    </>
  );
  if (inline) return (
    <div className="bi-panel" data-testid="po-form-dialog">
      <h2 className="bi-panel-title" style={{ color: "#0b5a2e" }}>{order ? `Edit ${order.ref}` : "New Pickup Order"}</h2>
      {body}
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="po-form-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-[#7a0f1d]">{order ? `Edit ${order.ref}` : "New Pickup Order"}</DialogTitle></DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
